import PropTypes from "prop-types";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import { formatMoney } from "@/utils/formatMoney";
import PaginatedList from "../Pagination";
import useIsMobile from "@/hooks/useIsMobile";
import {getCurrentRegion, isSubscriptionRegion} from "@/utils/regions";

const MAX_PRODUCTS_MOBILE = 3;
const TYPING_TIMEOUT = 1000;
// Delivery-interval units, longest first so "month" is not shadowed by a stray match.
// Values are days, only ever used to rank plans against each other.
const INTERVAL_UNITS = [
  [/month|monat/, 30],
  [/week|woche/, 7],
  [/day|tag/, 1],
];

/**
 * ISO country of the market being browsed, e.g. "DE", "FR".
 *
 * Shopify sets this per market domain, and it is the same code that feeds
 * `localization.country.iso_code` in Liquid — so the market rules below stay in
 * step with sections/main-collection.liquid. Validated because it is interpolated
 * into a GraphQL enum.
 */
function getMarketCountry() {
  const country = window.Shopify?.country;
  return /^[A-Z]{2}$/.test(country || "") ? country : null;
}

const Search = ({suggestions, isInPageSearch = false}) => {
  const {t} = useTranslation();
  const queryParams = new URLSearchParams(document.location.search);
  const qQueryString = queryParams.get("q");

  const isMobile = useIsMobile();

  const [query, setQuery] = useState(qQueryString || "");
  const [inputValue, setInputValue] = useState(qQueryString || "");
  const [isSearching, setIsSearching] = useState(false);
  const [products, setProducts] = useState(null);
  const [articles, setArticles] = useState(null);
  const [maxProductsToShow, setMaxProductsToShow] = useState(
    isMobile ? MAX_PRODUCTS_MOBILE : 999,
  );
  const typingTimeoutRef = useRef(null);
  const totalResultCount =
    products === null ? null : articles.length + products.length;

  useEffect(() => {
    getSearchResults(query, setProducts, setArticles, setIsSearching);
    updateQueryParameter("q", query);
  }, [query]);

  const handleInputChange = useCallback(value => {
    setIsSearching(true);
    setInputValue(value);

    // Clear previous timeout if any
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set a new timeout to delay the setQuery call
    typingTimeoutRef.current = setTimeout(() => {
      setQuery(value); // Update query after x miliseconds
    }, TYPING_TIMEOUT);
  }, []);

  const handleSuggestionClick = suggestion => {
    setQuery(suggestion);
    setInputValue(suggestion);
  };

  if (isInPageSearch) {
    return (
      <div className="container max-h-[calc(100vh-110px)] overflow-auto pt-[2px] text-deep-green">
        <SearchBar
          isSearching={isSearching}
          inputValue={inputValue}
          handleInputChange={handleInputChange}
          totalResultCount={totalResultCount}
          handleSuggestionClick={handleSuggestionClick}
          suggestions={suggestions}
          query={query}
          isInPageSearch={isInPageSearch}
          t={t}
        />
        {totalResultCount > 0 && (
          <>
            <div className="mt-4 flex items-center gap-16">
              {!!products?.length && (
                <div className="flex w-1/2 gap-8 flex-col lg:flex-row items-center">
                  {products.slice(0, 2).map(product => (
                    <ProductCard
                      product={product}
                      t={t}
                      key={`searchProduct${product.id}`}
                      isInPageSearch={isInPageSearch}
                    />
                  ))}
                    <a
                      href={`/search?q=${query}`}
                      className="button button--primary mb-6 mt-4 text-center lg:hidden"
                    >
                      {`${t("action.see_al_results")} (${totalResultCount})`}
                    </a>
                </div>
              )}
              {!!articles?.length && (
                <div className="flex w-1/2 flex-col gap-4">
                  {articles.slice(0, 3).map(article => (
                    <ArticleCard
                      article={article}
                      t={t}
                      key={`searchArticle${article.id}`}
                      isInPageSearch={isInPageSearch}
                    />
                  ))}
                </div>
              )}
            </div>

            <a
              href={`/search?q=${query}`}
              className="button button--primary mb-6 mt-4 text-center hidden lg:inline-block"
            >
              {`${t("action.see_al_results")} (${totalResultCount})`}
            </a>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="search-grid container pt-4 text-deep-green md:gap-x-24">
      <SearchBar
        isSearching={isSearching}
        inputValue={inputValue}
        handleInputChange={handleInputChange}
        totalResultCount={totalResultCount}
        handleSuggestionClick={handleSuggestionClick}
        suggestions={suggestions}
        query={query}
        t={t}
      />
      {!!products?.length && (
        <div className="search-products mb-4 mt-4 grid auto-rows-min grid-cols-2 items-end gap-8">
          <div className="col-span-2 flex justify-center md:justify-between">
            <h2 className="text-center text-[32px]">{t("common.products")}</h2>
            <a
              href="/collections/all"
              className="button button--tertiary col-span-2 hidden text-center md:flex"
            >
              {t("action.shop_all")}
            </a>
          </div>
          <PaginatedList
            itemsPerRow={isMobile ? 1 : 4}
            rows={isMobile ? 999 : 1}
            className="col-start-1 col-end-3"
          >
            {products.slice(0, maxProductsToShow).map(product => (
              <ProductCard
                product={product}
                t={t}
                key={`searchProduct${product.id}`}
              />
            ))}
          </PaginatedList>
          {maxProductsToShow < products.length ? (
            <button
              className="button button--tertiary col-span-2 text-center"
              onClick={() => setMaxProductsToShow(999)}
            >
              {t("search.show_all")}
            </button>
          ) : (
            <a
              href="/collections/all"
              className="button button--tertiary col-span-2 text-center md:hidden"
            >
              {t("action.shop_all")}
            </a>
          )}
          {articles?.length && <hr className="col-span-2" />}
        </div>
      )}

      {!!articles?.length && (
        <div className="search-articles mt-4 flex flex-col gap-8">
          <div className="col-span-2 mb-4 flex justify-center md:justify-between">
            <h2 className="text-center text-[32px]">{t("common.ph_blog")}</h2>
            <a
              href="/blog"
              className="button button--tertiary col-span-2 hidden text-center md:flex"
            >
              {t("action.read_blog")}
            </a>
          </div>
          <PaginatedList
            itemsPerRow={isMobile ? 1 : 4}
            rows={isMobile ? 3 : 2}
            className="col-start-1 col-end-3"
          >
            {articles.map(article => (
              <ArticleCard
                article={article}
                t={t}
                key={`searchArticle${article.id}`}
              />
            ))}
          </PaginatedList>
          <a
            href="/blog"
            className="button button--tertiary text-center md:hidden"
          >
            {t("action.read_blog")}
          </a>
        </div>
      )}
    </div>
  );
};

const SearchBar = ({
  isSearching,
  inputValue,
  handleInputChange,
  totalResultCount,
  handleSuggestionClick,
  suggestions,
  isInPageSearch,
  query,
  t,
}) => (
  <>
    <div className="search-bar mb-6">
      <label
        className={`search-bar search-bar-icon ${isSearching ? "search-bar-icon--loading" : ""}`}
      >
        <input
          type="text"
          placeholder={t("action.search_brand")}
          value={inputValue}
          autoFocus
          className={`w-full rounded-full bg-white leading-[52px] outline-dark-green ${isInPageSearch ? "pl-[72px] pr-6" : "px-6"}`}
          onChange={event => handleInputChange(event.target.value)}
        />
        {isInPageSearch && (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute right-[30px] top-[-2px] cursor-pointer"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("search:dropdown:toggle"))
            }
          >
            <path
              d="M1.72392 23.9413L12.3906 13.2747L23.0572 23.9413L23.9999 22.9987L13.3332 12.332L23.9999 1.66532L23.0572 0.722656L12.3906 11.3893L1.72392 0.722656L0.78125 1.66532L11.4479 12.332L0.78125 22.9987L1.72392 23.9413Z"
              fill="#0B2E24"
            />
          </svg>
        )}
      </label>
    </div>
    {totalResultCount === 0 && <NoResults query={query} t={t} />}
    {totalResultCount > 0 && (
      <div className="search-total-results mb-2 text-center text-[14px]">{t("search.results_found", {count: totalResultCount})}</div>
    )}
    {!totalResultCount > 0 && (
      <div className="pb-8 leading-loose">
        <div className="text-[14px] font-semibold">Vorgeschlagen</div>
        {suggestions?.map(suggestion => (
          <div
            className="suggestion-arrow w-max cursor-pointer text-[24px]"
            key={`${suggestion}-item`}
            onClick={() => {
              handleSuggestionClick(suggestion);
            }}
          >
            {suggestion}
          </div>
        ))}
      </div>
    )}
    {totalResultCount === 0 && (
      <a
        href="/collections/all"
        className="button button--primary mt-4 text-center"
      >
        {t("action.shop_brand")}
      </a>
    )}
  </>
);

const NoResults = ({query}) => (
  <div className="search-no-results mb-8">
    <div className="text-[32px]">{`Hoppla, es gibt keine Ergebnisse für „${query}“.`}</div>
  </div>
);

const ProductCard = ({product, t, isInPageSearch}) => (
  <div
    className={`product-item flex flex-col gap-2 ${isInPageSearch ? "w-7/12 lg:w-1/2" : ""}`}
  >
    <a
      href={getLocalizedOnlineStoreUrl(product.onlineStoreUrl)}
      aria-label={product.title}
      className="relative block aspect-square max-h-[300px] w-full overflow-hidden"
    >
      <img
        sizes=""
        src={product.featuredImage?.url}
        alt={product.featuredImage?.altText}
        loading="lazy"
        className="image object-contain hover:opacity-70"
      />
    </a>
    <div className="flex justify-between">
      <div className="font-semibold">{product.title}</div>
      <div>{getDefaultPlanPriceFromVariants(product.variants)}</div>
    </div>
    <a
      href={getLocalizedOnlineStoreUrl(product.onlineStoreUrl)}
      className="button button--secondary text-center"
    >
      Produkt ansehen
    </a>
  </div>
);

const ArticleCard = ({article, t, isInPageSearch}) => (
  <div className={`flex flex-col ${isInPageSearch ? "gap-2" : "gap-4"}`}>
    <div
      className={`font-semibold max-md:-mb-2 ${isInPageSearch ? "text-[20px]" : "text-[20px] md:text-[24px]"}`}
    >
      {article.title}
    </div>
    <div className={`${isInPageSearch ? "text-[14px]" : "text-[16px]"}`}>
      {article.content}
    </div>
    <a
      href={getLocalizedOnlineStoreUrl(article.onlineStoreUrl)}
      className="button button--secondary text-center md:hidden"
    >
      {t("action.read_more")}
    </a>
    <a
      href={getLocalizedOnlineStoreUrl(article.onlineStoreUrl)}
      className={`hidden underline md:block ${isInPageSearch ? "text-[14px]" : ""}`}
    >
      {t("action.read_more")}
    </a>
  </div>
);

function getLocalizedOnlineStoreUrl(onlineStoreUrl) {
  if (!onlineStoreUrl) return onlineStoreUrl;
  try {
    const url = new URL(onlineStoreUrl);
    // Keep the path/query/hash but let the current domain handle locale.
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    console.warn("Invalid onlineStoreUrl", onlineStoreUrl, error);
    return onlineStoreUrl;
  }
}

/**
 * Delivery interval of a selling plan, in days, or null if it can't be read.
 *
 * Used only to rank plans, so the day conversion needs to order them correctly, not
 * to be a calendar. Reading the number and its unit rather than matching a fixed
 * label keeps this working when a plan is renamed or a new cadence is added: the EU
 * store says "Alle 30 Tage" / "30 days" and the US one "Delivery every 1 month",
 * and both resolve without a per-store label list to keep in sync.
 */
function planIntervalDays(plan) {
  const label = `${plan.name || ""} ${plan.optionValue || ""}`.toLowerCase();
  const count = Number((label.match(/\d+/) || [])[0]);
  if (!Number.isFinite(count) || count <= 0) return null;
  const unit = INTERVAL_UNITS.find(([pattern]) => pattern.test(label));
  return count * (unit ? unit[1] : 1);
}

/**
 * Price shown on a search result card.
 *
 * Quotes what the product card quotes, so the two never disagree.
 *
 * ES/IT/UK do not sell subscriptions, and the card ignores the selling plan there
 * (see ProductCardPrice) — but the Storefront API still returns allocations for
 * those markets, so quoting a plan price advertised a discount the customer cannot
 * take: £28.79 against a card reading £36.09.
 *
 * Where subscriptions do exist, taking the largest discount across the plans is
 * wrong on the tiered products (20/25/30%), because the deepest tier belongs to the
 * least frequent plan — it advertised 25,26 € against a card price of 28,87 €.
 * Picking the shortest interval also avoids trusting allocation order, which Shopify
 * does not guarantee.
 */
function getDefaultPlanPriceFromVariants(variantArray = []) {
  const subscriptionsAvailable = isSubscriptionRegion(getCurrentRegion());

  const prices = variantArray
    .map(variant => {
      if (!subscriptionsAvailable) return Number(variant.price?.amount);
      const ranked = (variant.sellingPlans || [])
        .map(plan => ({plan, interval: planIntervalDays(plan)}))
        .filter(({plan, interval}) => interval !== null && plan.price != null)
        .sort((a, b) => a.interval - b.interval);
      return Number(ranked[0]?.plan.price ?? variant.price?.amount);
    })
    .filter(price => Number.isFinite(price));

  if (!prices.length) return null;
  return formatMoney(Math.round(Math.min(...prices) * 100));
}

const getSearchResults = (
  question,
  setProducts,
  setArticles,
  setIsSearching,
) => {
  if (!question) {
    setArticles(null);
    setProducts(null);
    setIsSearching(false);
    return;
  }
  // Scope the search to the market being browsed. Without this the Storefront API
  // answers for the whole store, so products that are not sold in this market come
  // back as results — the -pt collagen range is DE/AT/CH only, and was surfacing on
  // FR/IT/ES/GB where it cannot be bought.
  const country = getMarketCountry();
  const inContext = country ? ` @inContext(country: ${country})` : "";
  const query = `query${inContext} {
    search(query: "${question}", first: 100) {
      edges {
        node {
          ... on Product {
            id
            title
            tags
            availableForSale
            onlineStoreUrl
            featuredImage {
              url
              altText
              width
              height
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  sku
                  price {
                    amount
                    currencyCode
                  }
                  sellingPlanAllocations(first: 100) {
                    edges {
                      node {
                        sellingPlan {
                          id
                          name
                          options {
                            value
                          }
                        }
                        priceAdjustments {
                          price {
                            amount
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ... on Page {
            id
            title
          }
          ... on Article {
            id
            title
            onlineStoreUrl
            content(truncateAt: 200)
          }
        }
      }
    }
  }`;

  // Collections are global objects shared by every market, so a product retired in
  // one market is pulled from the grid there by a `hide-in-<country>` tag rather
  // than by leaving the collection (see sections/main-collection.liquid). Search
  // reads the same tag, otherwise a retired product still shows up here — the OG
  // collagen range is tagged hide-in-de / hide-in-ch and was appearing in DE and CH
  // search next to its -pt replacement.
  const marketHideTag = country ? `hide-in-${country.toLowerCase()}` : null;

  const endpoint = `https://${window.Shopify.shop}/api/2024-07/graphql.json`;
  const headers = {
    "Content-Type": "application/graphql",
    "X-Shopify-Storefront-Access-Token": window.Theme.api_key,
  };

  fetch(endpoint, {
    method: "POST",
    headers,
    body: query,
  })
    .then(response => response.json())
    .then(data => {
      const results = data?.data?.search?.edges || [];
      const products = [];
      const articles = [];
      results.forEach(({node}) => {
        if (node.id.startsWith("gid://shopify/Product/")) {
          if (products.length < 100) {
            products.push({
              id: node.id,
              title: node.title,
              featuredImage: node.featuredImage,
              sku: node.variants.edges[0].node.sku,
              isMainProduct:
                node.tags.includes("is_main_product") &&
                !node.tags.includes("conditionally-hidden") &&
                !(marketHideTag && node.tags.includes(marketHideTag)),
              onlineStoreUrl: node.onlineStoreUrl,
              variants: node.variants.edges.map(({node}) => ({
                id: node.id,
                price: {
                  amount: node.price.amount,
                  currencyCode: node.price.currencyCode,
                },
                sellingPlans: node.sellingPlanAllocations?.edges.map(
                  ({node}) => ({
                    id: node.sellingPlan.id,
                    name: node.sellingPlan.name,
                    optionValue: node.sellingPlan.options?.[0]?.value,
                    price: node.priceAdjustments?.[0]?.price?.amount,
                  }),
                ),
              })),
            });
          }
        } else if (node.id.startsWith("gid://shopify/Article/")) {
          if (articles.length < 100) {
            articles.push({...node});
          }
        }
      });
      setArticles(articles);
      setProducts(products.filter(el => !!el.onlineStoreUrl).filter(product => product.isMainProduct));
      setIsSearching(false);
      return;
    })
    .catch(error => {
      setIsSearching(false);
      console.error(error);
    });
};

function updateQueryParameter(key, value) {
  const url = new URL(window.location);
  if (value) {
    url.searchParams.set(key, value);
  } else {
    url.searchParams.delete(key);
  }

  // Update the URL in the browser without reloading
  window.history.replaceState({}, "", url);
}

SearchBar.propTypes = {
  suggestions: PropTypes.array,
  isSearching: PropTypes.bool,
  isInPageSearch: PropTypes.bool,
  inputValue: PropTypes.string,
  handleInputChange: PropTypes.func,
  totalResultCount: PropTypes.number,
  handleSuggestionClick: PropTypes.func,
  query: PropTypes.string,
  t: PropTypes.func,
};

NoResults.propTypes = {
  query: PropTypes.string,
  t: PropTypes.func,
};

ProductCard.propTypes = {
  product: PropTypes.object,
  t: PropTypes.func,
  isInPageSearch: PropTypes.bool,
};

ArticleCard.propTypes = {
  article: PropTypes.object,
  t: PropTypes.func,
  isInPageSearch: PropTypes.bool,
};

Search.propTypes = {
  suggestions: PropTypes.array,
  isInPageSearch: PropTypes.bool,
};

export default Search;
